import * as React from 'react';
import { Pencil } from 'lucide-react';
import type { EnumDef, RateUnit } from '@clawhire/shared';
import { cn } from '@/lib/utils';
import {
  EMPTY,
  enumOptions,
  formatBool,
  formatDate,
  formatList,
  formatPct,
  formatRate,
  type Option,
} from '@/lib/format';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { DateInput } from '@/components/ui/date-input';
import { SimpleSelect } from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { EnumBadge } from '@/components/data/EnumBadge';

/** One row of the properties grid. `key` is the field name sent in the PATCH body. */
export type PropertyField =
  | {
      key: string;
      label: string;
      type: 'text';
      value: string | null | undefined;
      placeholder?: string;
      editable?: boolean;
      help?: string;
    }
  | {
      key: string;
      label: string;
      type: 'textarea';
      value: string | null | undefined;
      placeholder?: string;
      editable?: boolean;
      help?: string;
    }
  | {
      key: string;
      label: string;
      type: 'number';
      value: number | null | undefined;
      min?: number;
      max?: number;
      step?: number;
      suffix?: string;
      editable?: boolean;
      help?: string;
    }
  | {
      key: string;
      label: string;
      type: 'rate';
      value: number | null | undefined;
      unit?: RateUnit | null;
      editable?: boolean;
      help?: string;
    }
  | {
      key: string;
      label: string;
      type: 'pct';
      value: number | null | undefined;
      editable?: boolean;
      help?: string;
    }
  | {
      key: string;
      label: string;
      type: 'date';
      value: string | null | undefined;
      editable?: boolean;
      help?: string;
    }
  | {
      key: string;
      label: string;
      type: 'boolean';
      value: boolean | null | undefined;
      editable?: boolean;
      help?: string;
    }
  | {
      key: string;
      label: string;
      type: 'enum';
      value: string | null | undefined;
      def: EnumDef<string>;
      options?: readonly string[];
      allowEmpty?: boolean;
      editable?: boolean;
      help?: string;
    }
  | {
      key: string;
      label: string;
      type: 'select';
      value: string | null | undefined;
      options: ReadonlyArray<Option>;
      allowEmpty?: boolean;
      editable?: boolean;
      help?: string;
    }
  | {
      key: string;
      label: string;
      type: 'list';
      value: readonly string[] | null | undefined;
      editable?: boolean;
      help?: string;
    }
  | {
      key: string;
      label: string;
      type: 'custom';
      value?: unknown;
      render: () => React.ReactNode;
      editable?: false;
      help?: string;
    };

export interface PropertiesPanelProps {
  fields: PropertyField[];
  /** Persist a change: receives `{ [key]: newValue }`. Reject to keep the old value and show the error. */
  onSave?: (patch: Record<string, unknown>) => Promise<unknown> | void;
  /** Turn editing off globally (viewer role). */
  readOnly?: boolean;
  columns?: 1 | 2;
  className?: string;
}

/**
 * Label/value grid with inline editing: click a value (or focus it and press Enter) to edit;
 * saving happens on blur or Enter, Esc cancels, nothing is sent when the value did not change.
 */
export function PropertiesPanel({
  fields,
  onSave,
  readOnly = false,
  columns = 2,
  className,
}: PropertiesPanelProps) {
  return (
    <dl
      className={cn(
        'grid gap-x-6 gap-y-3',
        columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1',
        className,
      )}
    >
      {fields.map((field) => (
        <PropertyRow key={field.key} field={field} onSave={onSave} readOnly={readOnly} />
      ))}
    </dl>
  );
}

function displayValue(field: PropertyField): React.ReactNode {
  switch (field.type) {
    case 'text':
    case 'textarea':
      return field.value ? <span className="whitespace-pre-wrap">{field.value}</span> : null;
    case 'number':
      return field.value === null || field.value === undefined
        ? null
        : `${field.value}${field.suffix ? ` ${field.suffix}` : ''}`;
    case 'rate':
      return field.value === null || field.value === undefined ? null : (
        <span className="tabular-nums">{formatRate(field.value, field.unit ?? 'HOUR')}</span>
      );
    case 'pct':
      return field.value === null || field.value === undefined ? null : (
        <span className="tabular-nums">{formatPct(field.value)}</span>
      );
    case 'date':
      return field.value ? <time dateTime={field.value}>{formatDate(field.value)}</time> : null;
    case 'boolean':
      return field.value === null || field.value === undefined ? null : formatBool(field.value);
    case 'enum':
      return field.value ? <EnumBadge def={field.def} value={field.value} /> : null;
    case 'select':
      return field.value ? (field.options.find((o) => o.value === field.value)?.label ?? field.value) : null;
    case 'list':
      return field.value && field.value.length > 0 ? formatList(field.value, 10) : null;
    case 'custom':
      return field.render();
    default:
      return null;
  }
}

function PropertyRow({
  field,
  onSave,
  readOnly,
}: {
  field: PropertyField;
  onSave?: PropertiesPanelProps['onSave'];
  readOnly: boolean;
}) {
  const editable = !readOnly && Boolean(onSave) && field.editable !== false && field.type !== 'custom';
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const shown = displayValue(field);
  const labelId = `prop-${field.key}-label`;

  const commit = async (next: unknown) => {
    setEditing(false);
    if (
      Object.is(next, field.value) ||
      (next === null && (field.value === null || field.value === undefined))
    )
      return;
    if (
      field.type === 'list' &&
      Array.isArray(next) &&
      Array.isArray(field.value) &&
      next.join('\u0000') === field.value.join('\u0000')
    )
      return;
    setSaving(true);
    try {
      await onSave?.({ [field.key]: next });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const wide = field.type === 'textarea';

  return (
    <div className={cn('min-w-0', wide && 'sm:col-span-2')}>
      <dt
        id={labelId}
        className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
        title={field.help}
      >
        {field.label}
      </dt>
      <dd className="mt-0.5 text-sm">
        {editing && editable ? (
          <Editor field={field} onCommit={commit} onCancel={() => setEditing(false)} labelId={labelId} />
        ) : editable ? (
          <button
            type="button"
            className={cn(
              'group -mx-1.5 flex w-[calc(100%+0.75rem)] items-start justify-between gap-2 rounded px-1.5 py-0.5 text-left hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              saving && 'opacity-60',
            )}
            onClick={() => setEditing(true)}
            aria-labelledby={labelId}
            aria-busy={saving || undefined}
            disabled={saving}
          >
            <span className={cn('min-w-0 flex-1 break-words', shown === null && 'text-muted-foreground')}>
              {shown ?? EMPTY}
            </span>
            <Pencil
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
              aria-hidden="true"
            />
          </button>
        ) : (
          <span className={cn('block break-words', shown === null && 'text-muted-foreground')}>
            {shown ?? EMPTY}
          </span>
        )}
      </dd>
    </div>
  );
}

function Editor({
  field,
  onCommit,
  onCancel,
  labelId,
}: {
  field: PropertyField;
  onCommit: (value: unknown) => void;
  onCancel: () => void;
  labelId: string;
}) {
  const keyHandler = (e: React.KeyboardEvent, current: () => unknown, multiline = false) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    } else if (e.key === 'Enter' && (!multiline || e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onCommit(current());
    }
  };

  switch (field.type) {
    case 'text':
      return (
        <TextEditor
          initial={field.value ?? ''}
          placeholder={field.placeholder}
          labelId={labelId}
          onCommit={(v) => onCommit(v.trim() === '' ? null : v.trim())}
          keyHandler={keyHandler}
        />
      );
    case 'textarea':
      return (
        <TextEditor
          initial={field.value ?? ''}
          placeholder={field.placeholder}
          labelId={labelId}
          multiline
          onCommit={(v) => onCommit(v.trim() === '' ? null : v)}
          keyHandler={keyHandler}
        />
      );
    case 'list':
      return (
        <TextEditor
          initial={(field.value ?? []).join(', ')}
          placeholder="Comma-separated"
          labelId={labelId}
          onCommit={(v) =>
            onCommit(
              v
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
          keyHandler={keyHandler}
        />
      );
    case 'number':
    case 'rate':
    case 'pct':
      return (
        <NumberEditor
          initial={field.value ?? null}
          labelId={labelId}
          min={field.type === 'number' ? field.min : 0}
          max={field.type === 'number' ? field.max : field.type === 'pct' ? 99.99 : undefined}
          step={field.type === 'number' ? field.step : 0.01}
          onCommit={onCommit}
          keyHandler={keyHandler}
        />
      );
    case 'date':
      return (
        <DateInput
          autoFocus
          value={field.value ?? null}
          aria-labelledby={labelId}
          onChange={(iso) => onCommit(iso)}
          onBlur={onCancel}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancel();
          }}
        />
      );
    case 'boolean':
      return (
        <div className="flex h-9 items-center">
          <Switch
            autoFocus
            checked={Boolean(field.value)}
            aria-labelledby={labelId}
            onCheckedChange={(v) => onCommit(v)}
            onBlur={onCancel}
          />
        </div>
      );
    case 'enum':
    case 'select': {
      const options = field.type === 'enum' ? enumOptions(field.def, field.options) : field.options;
      return (
        <SelectEditor
          value={field.value ?? null}
          options={options}
          allowEmpty={field.allowEmpty ?? false}
          labelId={labelId}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      );
    }
    default:
      return null;
  }
}

function TextEditor({
  initial,
  placeholder,
  labelId,
  multiline = false,
  onCommit,
  keyHandler,
}: {
  initial: string;
  placeholder?: string;
  labelId: string;
  multiline?: boolean;
  onCommit: (v: string) => void;
  keyHandler: (e: React.KeyboardEvent, current: () => unknown, multiline?: boolean) => void;
}) {
  const [value, setValue] = React.useState(initial);
  const common = {
    autoFocus: true,
    value,
    placeholder,
    'aria-labelledby': labelId,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setValue(e.target.value),
    onBlur: () => onCommit(value),
    onKeyDown: (e: React.KeyboardEvent) => keyHandler(e, () => value, multiline),
  };
  return multiline ? <Textarea rows={4} {...common} /> : <Input {...common} className="h-8" />;
}

function NumberEditor({
  initial,
  labelId,
  min,
  max,
  step,
  onCommit,
  keyHandler,
}: {
  initial: number | null;
  labelId: string;
  min?: number;
  max?: number;
  step?: number;
  onCommit: (v: number | null) => void;
  keyHandler: (e: React.KeyboardEvent, current: () => unknown) => void;
}) {
  const [value, setValue] = React.useState(initial === null ? '' : String(initial));
  const parse = (): number | null => {
    const t = value.trim().replace(',', '.');
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : initial;
  };
  return (
    <Input
      autoFocus
      type="number"
      inputMode="decimal"
      value={value}
      min={min}
      max={max}
      step={step ?? 'any'}
      aria-labelledby={labelId}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => onCommit(parse())}
      onKeyDown={(e) => keyHandler(e, parse)}
      className="h-8 w-40 tabular-nums"
    />
  );
}

function SelectEditor({
  value,
  options,
  allowEmpty,
  labelId,
  onCommit,
  onCancel,
}: {
  value: string | null;
  options: ReadonlyArray<Option>;
  allowEmpty: boolean;
  labelId: string;
  onCommit: (v: string | null) => void;
  onCancel: () => void;
}) {
  // Radix Select opens on mount via the trigger's auto focus + Enter; we commit on change.
  return (
    <div
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
    >
      <SimpleSelect
        value={value}
        options={options}
        allowEmpty={allowEmpty}
        aria-labelledby={labelId}
        onChange={(v) => onCommit(v)}
        className="h-8 w-56"
      />
    </div>
  );
}
